import React, { useMemo, useState } from 'react';
import { FaPaperclip } from 'react-icons/fa';
import apiService from '../services/apiService';
import usePaymentsOneBillId from '../hooks/usePaymentsOneBillId';
import { sortByDate } from '../utils/sortByDate';
import {
  LINKED_NOTICE,
  LINK_FIELD_KEYS,
  UNLINKED_NOTICE,
  deletePaymentWarning,
  linkedParentIds,
  readPaymentLinks,
} from '../utils/paymentLink';
import {
  ATTACHMENT_FIELD_KEYS,
  attachmentCount,
  isAttachmentField,
} from '../utils/paymentAttachments';
import { LinkPaymentPrompt, PaymentLinkCell } from './PaymentLinkControls';

const PaymentsTable = ({
  bill,
  onAdd,
  onEdit,
  onDelete,
  fixedValues = {},
  refreshData
}) => {
  const excludedFields = Object.keys(fixedValues);
  const [editedPayment, setEditedPayment] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [sortDir, setSortDir] = useState('desc');
  // Linked payments: the link picker (opened after an add or from a row), the
  // inline unlink confirm, and a one-line notice (this table has no flash
  // message of its own). A payment can hold several links, so the pending
  // confirm has to name both the payment and the single link being removed.
  const [linkPromptPaymentId, setLinkPromptPaymentId] = useState(null);
  const [pendingUnlink, setPendingUnlink] = useState(null);
  const [unlinkBusy, setUnlinkBusy] = useState(false);
  const [linkNotice, setLinkNotice] = useState('');
  // Delete used to fire the instant the button was pressed — nothing asked,
  // nothing said. It now arms an inline confirm that spells out whether this
  // ends the payment or only removes it from this bill.
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const { payments, loading, error } = usePaymentsOneBillId(bill?._id, refreshKey);

  // Sort a NEW array by payment date (newest first by default). All the
  // index-based handlers below operate on this same sorted view so edit/delete
  // continue to target the correct row.
  const sortedPayments = useMemo(
    () => sortByDate(Array.isArray(payments) ? payments : [], 'payment_date', sortDir),
    [payments, sortDir]
  );
  const toggleSortDir = () => setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));

  const fields = useMemo(() => {
    let baseFields = [];
    if (Array.isArray(payments) && payments.length > 0) {
      baseFields = Object.keys(payments[0]).filter(
        field =>
          field !== 'Id' &&
          !excludedFields.includes(field) &&
          // Link fields get their own column below — they are not editable text.
          !LINK_FIELD_KEYS.includes(field) &&
          // Nor are the receipts. This endpoint Title-Cases its keys, so they
          // arrive as `Attachments`, and as a derived column the array became an
          // editable input reading "[object Object]".
          !isAttachmentField(field)
      );
    } else {
      baseFields = ['Payment Date', 'Payment Amount', 'Payment Charge', 'Payment Method', 'Remarks'];
    }
    
    // Ensure Payment Charge is always included
    const hasPaymentCharge = baseFields.some(f => f.toLowerCase().includes('charge'));
    if (!hasPaymentCharge) {
      baseFields.splice(2, 0, 'Payment Charge');
    }
    
    return baseFields;
  }, [payments, excludedFields]);

  // ✅ Recalculate initial state when fields change
  const initialPaymentState = useMemo(() => {
    return {
      ...fields.reduce((acc, field) => {
        acc[field] = '';
        return acc;
      }, {}),
      ...fixedValues
    };
  }, [fields, fixedValues]);

  const [newPayment, setNewPayment] = useState(initialPaymentState);

  const handleChange = (e, field) => {
    setEditedPayment({
      ...editedPayment,
      [field]: e.target.value,
    });
  };

  const handleAdd = async () => {
    const requiredFields = Object.entries(newPayment).filter(
      ([key]) => key !== 'Remarks' && key !== 'Id' // exclude Remarks and Id from required check
    );

    const allRequiredFieldsFilled = requiredFields.every(([_, val]) =>
      val !== undefined && val !== null && val.toString().trim() !== ''
    );

    if (allRequiredFieldsFilled) {
      const completePayment = {
        ...newPayment,
        ...fixedValues, // includes Id if needed
      };

      // Snapshot the ids we already have: the add endpoint answers with a
      // message only, so the payment just created is the one that wasn't here.
      const beforeIds = new Set(
        (Array.isArray(payments) ? payments : []).map((p) => p?.Id).filter(Boolean)
      );

      try {
        const added = await onAdd(completePayment);
        setNewPayment(initialPaymentState);
        setRefreshKey((prev) => prev + 1);
        setPendingUnlink(null);
        setLinkNotice('');

        if (added !== false) {
          const after = await apiService.getBillPayments(bill);
          const created = (Array.isArray(after) ? after : []).find(
            (p) => p?.Id && !beforeIds.has(p.Id)
          );
          setLinkPromptPaymentId(created?.Id || null);
        }
      } catch (err) {
        console.error("Add failed:", err);
      }
    } else {
      console.warn("Please fill in all required fields.");
    }
  };

  // A bill payment can only ever be linked to a subscription.
  const loadSubscriptionOptions = async () => {
    const response = await apiService.getSubscriptions(1, 500);
    const data = response?.data || response;
    const items = Array.isArray(data?.items) ? data.items : [];
    return items
      .filter((s) => s?._id)
      .map((s) => ({ id: s._id, label: s.name || s.biller || 'Untitled subscription' }));
  };

  // Adds ONE link and leaves the picker open — it offers "Link another" so the
  // same payment can settle several subscriptions in one pass.
  const handleLinkPayment = async (subscriptionId) => {
    await apiService.linkBillPayment(bill._id, linkPromptPaymentId, {
      type: 'subscription',
      id: subscriptionId,
    });
    setLinkNotice(LINKED_NOTICE);
    setRefreshKey((prev) => prev + 1);
    if (typeof refreshData === 'function') refreshData();
  };

  // Removes just the one link. The payment itself, and its other links, stay.
  const handleUnlink = async (paymentId, linkId) => {
    setUnlinkBusy(true);
    try {
      await apiService.unlinkBillPayment(bill._id, paymentId, linkId);
      setPendingUnlink(null);
      setLinkNotice(UNLINKED_NOTICE);
      setRefreshKey((prev) => prev + 1);
      if (typeof refreshData === 'function') refreshData();
    } catch (err) {
      setLinkNotice(err?.response?.data?.detail || err?.message || 'Failed to unlink payment.');
    } finally {
      setUnlinkBusy(false);
    }
  };

  // The payment the picker is open for, so the picker can grey out the
  // subscriptions it is already linked to.
  const linkPromptPayment = sortedPayments.find((p) => p?.Id === linkPromptPaymentId);

  const handleEdit = (index) => {
    setEditingIndex(index);
    setPendingUnlink(null);
    setEditedPayment({ ...sortedPayments[index] }); // make a copy
  };

  const handleSave = async (bill) => {
    if (!editedPayment || !editedPayment.Id) return;

    try {
      // Prepare payment data with proper type conversion for numbers
      const paymentData = { ...editedPayment };

      // Link state belongs to the link/unlink endpoints — an edit must never
      // carry it back and risk clobbering the pair.
      LINK_FIELD_KEYS.forEach((key) => delete paymentData[key]);

      // Same for the receipts: they have their own upload/delete endpoints, and
      // PUTting the whole array back is one stale copy away from blanking it.
      ATTACHMENT_FIELD_KEYS.forEach((key) => delete paymentData[key]);

      // Convert payment_charge to number if it exists and is not empty
      if (paymentData.payment_charge !== undefined && paymentData.payment_charge !== null && paymentData.payment_charge !== '') {
        paymentData.payment_charge = parseFloat(paymentData.payment_charge);
      }
      
      // Convert payment_amount to number if it exists
      if (paymentData.payment_amount !== undefined && paymentData.payment_amount !== null) {
        paymentData.payment_amount = parseFloat(paymentData.payment_amount);
      }
      
      console.log("Sending payment data:", paymentData);
      
      await apiService.updatePayment(bill._id, editedPayment.Id, paymentData);

      // Notify parent to update table (or refetch from API)
      onEdit(editedPayment, editingIndex);

      // ✅ Trigger refresh
      if (typeof refreshData === 'function') {
        refreshData();
      }

      // Reset edit state
      setEditingIndex(null);
      setEditedPayment(null);
      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      console.error("Failed to update payment:", error);
      // Optionally show a flash message or toast
    }
  };

  // const handleDelete = async (index) => {
  //   const paymentToDelete = payments[index];
  //   if (paymentToDelete.Id) {
  //     onDelete(paymentToDelete.Id);
  //     setRefreshKey((prev) => prev + 1);
  //   } else {
  //     console.warn('No ID found for payment:', paymentToDelete);
  //   }
  // };

  const handleDelete = async (index) => {
    const paymentToDelete = sortedPayments?.[index];
    if (!paymentToDelete) return; // nothing to delete
    if (paymentToDelete.Id) {
      setDeleteBusy(true);
      try {
        await onDelete(paymentToDelete.Id); // ✅ wait for API
        setPendingUnlink(null);
        if (linkPromptPaymentId === paymentToDelete.Id) setLinkPromptPaymentId(null);
        setRefreshKey(prev => prev + 1);    // ✅ refresh after success
      } catch (err) {
        console.error('Delete failed:', err);
      } finally {
        setDeleteBusy(false);
        setPendingDeleteId(null);
      }
    } else {
      console.warn('No ID found for payment:', paymentToDelete);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <h4 style={{ margin: 0 }}>Payments</h4>
        <button
          type="button"
          onClick={toggleSortDir}
          title="Sort by payment date"
          style={{ padding: '2px 8px', cursor: 'pointer' }}
        >
          Date {sortDir === 'desc' ? '▼' : '▲'}
        </button>
      </div>

      {linkNotice && <p style={{ marginBottom: 0 }}>{linkNotice}</p>}

      {linkPromptPaymentId && (
        <LinkPaymentPrompt
          key={linkPromptPaymentId}
          targetLabel="Subscription"
          loadOptions={loadSubscriptionOptions}
          onLink={handleLinkPayment}
          onDismiss={() => setLinkPromptPaymentId(null)}
          alreadyLinkedIds={linkedParentIds(linkPromptPayment)}
        />
      )}

      <table border="1" cellPadding="8" style={{
        width: '100%',
        borderCollapse: 'collapse',
        tableLayout: 'auto'
      }}>
        <colgroup>
          {/* Fixed values columns */}
          {Object.keys(fixedValues).map((_, i) => (
            <col key={`col-fixed-${i}`} style={{ minWidth: '100px' }} />
          ))}
          {/* Dynamic field columns with special handling for Remarks */}
          {fields.map((field, idx) => (
            <col
              key={`col-${idx}`}
              style={{
                minWidth: field.toLowerCase().includes('remark') ? '150px' : '120px'
              }}
            />
          ))}
          {/* Receipts column */}
          <col style={{ minWidth: '110px' }} />
          {/* Link column */}
          <col style={{ minWidth: '180px' }} />
          {/* Actions column */}
          <col style={{ minWidth: '100px' }} />
        </colgroup>
        <thead>
          <tr>
            {Object.keys(fixedValues).map((field, i) => (
              <th key={`fixed-head-${i}`} style={{ wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                {field}
              </th>
            ))}
            {fields.map((field, idx) => (
              <th key={idx} style={{ wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                {field.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
              </th>
            ))}
            <th>Receipts</th>
            <th>Link</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {Array.isArray(sortedPayments) && sortedPayments.length > 0 ? (
            sortedPayments.map((payment, index) => (
              <tr key={index}>
                {Object.keys(fixedValues).map((field, i) => (
                  <td key={`fixed-${index}-${i}`} style={{
                    wordWrap: 'break-word',
                    whiteSpace: 'normal',
                    overflow: 'visible',
                    maxWidth: '200px'
                  }}>
                    <input
                      value={editingIndex === index ? (editedPayment?.[field] ?? '') : (payment?.[field] ?? '')}
                      onChange={(e) => editingIndex === index && handleChange(e, field)}
                      readOnly={editingIndex !== index}
                      style={{
                        backgroundColor: editingIndex === index ? 'white' : 'gray',
                        width: '100%',
                        boxSizing: 'border-box',
                        color: editingIndex === index ? 'black' : 'white',
                        cursor: editingIndex === index ? 'text' : 'default',
                        border: '1px solid black'
                      }}
                    />
                  </td>
                ))}
                {fields.map((field, i) => (
                  <td key={i} style={{
                    wordWrap: 'break-word',
                    whiteSpace: 'normal',
                    overflow: 'visible',
                    maxWidth: '200px'
                  }}>
                    <input
                      type={field.toLowerCase().includes("date") ? "date" : field.toLowerCase().includes("charge") ? "number" : field.toLowerCase().includes("amount") ? "number" : "text"}
                      step={field.toLowerCase().includes("charge") || field.toLowerCase().includes("amount") ? "0.01" : undefined}
                      value={editingIndex === index ? (editedPayment?.[field] ?? '') : (payment?.[field] ?? '')}
                      onChange={(e) => editingIndex === index && handleChange(e, field)}
                      readOnly={editingIndex !== index}
                      style={{
                        width: '100%',
                        boxSizing: 'border-box',
                        backgroundColor: editingIndex === index ? 'white' : 'black',
                        color: editingIndex === index ? 'black' : 'white',
                        cursor: editingIndex === index ? 'text' : 'default',
                        border: '1px solid black'
                      }}
                    />
                  </td>
                ))}
                <td>
                  {attachmentCount(payment) > 0 ? (
                    <span
                      title={`${attachmentCount(payment)} attachment${
                        attachmentCount(payment) === 1 ? '' : 's'
                      }`}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                    >
                      <FaPaperclip /> {attachmentCount(payment)}
                    </span>
                  ) : (
                    <span style={{ color: '#888' }}>—</span>
                  )}
                </td>
                <td>
                  <PaymentLinkCell
                    links={readPaymentLinks(payment)}
                    confirmingLinkId={
                      pendingUnlink?.paymentId === payment?.Id ? pendingUnlink.linkId : null
                    }
                    busy={unlinkBusy}
                    onRequestUnlink={(linkId) =>
                      setPendingUnlink({ paymentId: payment?.Id, linkId })
                    }
                    onConfirmUnlink={(linkId) => handleUnlink(payment?.Id, linkId)}
                    onCancelUnlink={() => setPendingUnlink(null)}
                    onRequestLink={
                      payment?.Id
                        ? () => {
                            setPendingUnlink(null);
                            setLinkNotice('');
                            setLinkPromptPaymentId(payment.Id);
                          }
                        : undefined
                    }
                  />
                </td>
                <td>
                  {payment?.Id && pendingDeleteId === payment.Id ? (
                    <div>
                      <div style={{ fontSize: '0.8rem', marginBottom: 4 }}>
                        {deletePaymentWarning(payment, 'this bill')}
                      </div>
                      <button
                        type="button"
                        onClick={() => handleDelete(index)}
                        disabled={deleteBusy}
                      >
                        {deleteBusy ? 'Deleting...' : 'Confirm'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setPendingDeleteId(null)}
                        disabled={deleteBusy}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <>
                      {editingIndex === index ? (
                        <button onClick={() => handleSave(bill)}>Save</button>
                      ) : (
                        <button onClick={() => handleEdit(index)}>Edit</button>
                      )}
                      <button
                        onClick={() => {
                          setPendingUnlink(null);
                          setPendingDeleteId(payment?.Id || null);
                        }}
                      >
                        Delete
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={fields.length + Object.keys(fixedValues).length + 3} style={{ textAlign: 'center' }}>
                No payments found
              </td>
            </tr>
          )}
          <tr>
            {Object.keys(fixedValues).map((field, i) => (
              <td key={`new-fixed-${i}`} style={{
                wordWrap: 'break-word',
                whiteSpace: 'normal',
                overflow: 'hidden'
              }}>
                <input
                  value={newPayment[field]}
                  readOnly
                  style={{
                    backgroundColor: 'gray',
                    width: '100%',
                    boxSizing: 'border-box'
                  }}
                />
              </td>
            ))}
            {fields.map((field, i) => (
              <td key={i} style={{
                wordWrap: 'break-word',
                whiteSpace: 'normal'
              }}>
                <input
                  type={field.toLowerCase().includes("date") ? "date" : field.toLowerCase().includes("charge") ? "number" : field.toLowerCase().includes("amount") ? "number" : "text"}
                  step={field.toLowerCase().includes("charge") || field.toLowerCase().includes("amount") ? "0.01" : undefined}
                  value={newPayment[field]}
                  onChange={(e) =>
                    setNewPayment({ ...newPayment, [field]: e.target.value })
                  }
                  style={{ width: '100%' }}
                />
              </td>
            ))}
            <td style={{ color: '#888' }}>After saving</td>
            <td style={{ color: '#888' }}>Link after saving</td>
            <td>
              <button onClick={handleAdd}>Add</button>
            </td>
          </tr>

        </tbody>
      </table>
    </div>
  );
};

export default PaymentsTable;
